import React, { useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
} from '@mui/material';
import { 
  Add as AddIcon,
  List as ListIcon,
  AccountCircle as AccountIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const DoctorDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return null;
  }

  const stats = {
    available: 0,
    completed: 0,
    inProgress: 0
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'medium' }}>
          Bem-vindo(a), Dr(a). {user.name}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Aqui está um resumo da sua atividade
        </Typography>
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/doctor/transcription/new')}
            >
              Nova Transcrição
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<ListIcon />}
              onClick={() => navigate('/doctor/transcriptions')}
            >
              Ver Transcrições
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              height: 140,
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PlayIcon sx={{ mr: 1 }} />
              <Typography component="h2" variant="h6">
                Transcrições Disponíveis
              </Typography>
            </Box>
            <Typography component="p" variant="h3">
              {stats.available}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              height: 140,
              bgcolor: 'warning.light',
              color: 'warning.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PauseIcon sx={{ mr: 1 }} />
              <Typography component="h2" variant="h6">
                Em Andamento
              </Typography>
            </Box>
            <Typography component="p" variant="h3">
              {stats.inProgress}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              height: 140,
              bgcolor: 'success.light',
              color: 'success.contrastText',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DoneIcon sx={{ mr: 1 }} />
              <Typography component="h2" variant="h6">
                Transcrições Concluídas
              </Typography>
            </Box>
            <Typography component="p" variant="h3">
              {stats.completed}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Plan Info */}
      {user.plan && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccountIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2">
                Informações do Plano
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography color="text.secondary" gutterBottom>
                  Plano Atual
                </Typography>
                <Typography variant="h6">
                  {user.plan.name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography color="text.secondary" gutterBottom>
                  Valor Mensal
                </Typography>
                <Typography variant="h6">
                  {user.plan.price ? `R$ ${user.plan.price.toFixed(2)}` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography color="text.secondary" gutterBottom>
                  Recursos
                </Typography>
                <Typography variant="body1">
                  {user.plan.features || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button size="small" color="primary">
              Ver Detalhes do Plano
            </Button>
          </CardActions>
        </Card>
      )}
    </Box>
  );
};

export default DoctorDashboard;
